// @flow

import type {$AxiosXHR} from 'axios';
import type {Dispute, PartnerEvidence, PaymentMethod} from '@containers/disputes/types';
import type {DisputeSort, Filter} from '@reducers/disputes/types';
import type {RawDispute, RawDisputeFeatures, RawPaymentMethod} from './types';

import Bottleneck from 'bottleneck';
import {disputesAPI as api} from '../../utils/api/afs-disputes-api';
import {MerchantNotSetError, normalizeMerchantId} from '../../utils/merchant';
import {convertDispute, convertDisputeFeature, convertPaymentMethod} from './converter';

// 4 concurrent; 60 requests per minute (auto refills)
const partnerEvidenceLimiter = new Bottleneck({
  maxConcurrent: 4,
  reservoir: 60,
  reservoirRefreshAmount: 60,
  reservoirRefreshInterval: 60_000
});

const wait = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

const MAX_PARTNER_EVIDENCE_RETRIES = 5;

/**
 * Reject immediately (without touching the network) when the merchant id is not
 * a usable positive integer. This prevents requests such as
 * `/publisher/null/dictionaries` when the master app did not provide a merchant.
 */
const withMerchantID = (merchantID: mixed, request: Function): Promise<any> => {
  const id = normalizeMerchantId(merchantID);
  if (id === null) {
    return Promise.reject(new MerchantNotSetError());
  }
  return request(id);
};

const getRetryAfterSeconds = (headers: any): ?number => {
  if (!headers) {
    return null;
  }
  const headerValue =
    typeof headers.get === 'function'
      ? headers.get('Retry-After') || headers.get('retry-after')
      : headers['retry-after'] || headers['Retry-After'];
  if (headerValue === undefined || headerValue === null) {
    return null;
  }
  const parsed = Number(headerValue);
  return Number.isNaN(parsed) ? 1 : parsed;
};

export const getPaymentMethods = (merchantID: number): Promise<Array<PaymentMethod>> =>
  withMerchantID(merchantID, id =>
    api
      .get(`publisher/${id}/dictionaries`)
      .then((response: $AxiosXHR<{result: Array<RawPaymentMethod>}>) =>
        response.data.result.map((res: RawPaymentMethod) => convertPaymentMethod(res))
      )
  );

export const getDisputesCount = (merchantID: number, params: Filter) =>
  withMerchantID(merchantID, id =>
    api
      .get(`publisher/${id}/disputes/count`, {params})
      .then((response: $AxiosXHR<{result: number}>) => response.data.result)
  );

export const getDisputeFeatures = (merchantID: number) =>
  withMerchantID(merchantID, id =>
    api
      .get(`publisher/${id}/features`)
      .then((response: $AxiosXHR<{data: RawDisputeFeatures}>) =>
        convertDisputeFeature(response.data)
      )
  );

export const getDisputes = (
  merchantID: number,
  params: Filter & {
    limit: number,
    offset: number,
    sort: DisputeSort
  }
) =>
  withMerchantID(merchantID, id =>
    api
      .get(`publisher/${id}/disputes`, {params})
      .then((response: $AxiosXHR<{result: Array<RawDispute>}>) =>
        response.data.result.map<Dispute>((res: RawDispute) => convertDispute(res))
      )
  );

// Fetch partner evidence for a specific dispute
const fetchPartnerEvidence = async (
  merchantID: number,
  disputeID: number,
  attempt: number = 0
): Promise<PartnerEvidence> => {
  try {
    const response: $AxiosXHR<any> = await partnerEvidenceLimiter.schedule(() =>
      api.get(`publisher/${merchantID}/disputes/${disputeID}/partner-evidence`)
    );
    const d = response?.data || {};
    return {
      days_left: d.days_left ?? d.DaysLeft ?? d.daysLeft ?? 0,
      status: d.status ?? d.Status ?? '',
      response_text: d.response_text ?? d.ResponseText ?? '',
      partner_files: d.partner_files ?? d.PartnerFiles ?? []
    };
  } catch (error) {
    const res = error?.response;
    if (res?.status === 429 && attempt < MAX_PARTNER_EVIDENCE_RETRIES) {
      const retryAfterSeconds = getRetryAfterSeconds(res.headers);
      if (retryAfterSeconds !== null && retryAfterSeconds !== undefined) {
        await wait((retryAfterSeconds || 1) * 1000);
      }
      return fetchPartnerEvidence(merchantID, disputeID, attempt + 1);
    }
    throw error;
  }
};

export const getPartnerEvidence = (
  merchantID: number,
  disputeID: number
): Promise<PartnerEvidence> =>
  withMerchantID(merchantID, id => fetchPartnerEvidence(id, disputeID));

export const getReportLink = (
  merchantID: number,
  params: Filter,
  language: string,
  translations?: ?Object
) => {
  const requestBody = {
    filters: params,
    language
  };

  if (translations && Object.keys(translations).length > 0) {
    requestBody.translations = translations;
  }

  return withMerchantID(merchantID, id =>
    api
      .post(`publisher/${id}/disputes/report`, requestBody)
      .then((response: $AxiosXHR<{url: string}>) => response.data.url)
  );
};

export const updateEvidenceRequest = (merchantID: number, requestID: number, req: any) =>
  withMerchantID(merchantID, id =>
    api
      .put(`publisher/${id}/partner-evidence-request/${requestID}`, req)
      .then((response: $AxiosXHR<{result: boolean}>) => response.data.result)
      .catch(error => {
        // Enhanced error handling for partner-evidence-request endpoint
        if (error.response) {
          const {status, data} = error.response;

          // Return translation keys instead of localized messages
          switch (status) {
            case 400:
              throw new Error(data?.message || 'app.support.disputes.errors.invalid-data');
            case 401:
              throw new Error('app.support.disputes.errors.auth-required');
            case 403:
              throw new Error('app.support.disputes.errors.access-denied');
            case 404:
              throw new Error(`app.support.disputes.errors.not-found:${requestID}`);
            case 409:
              throw new Error(data?.message || 'app.support.disputes.errors.already-processed');
            case 422:
              throw new Error(data?.message || 'app.support.disputes.errors.validation');
            case 429:
              throw new Error('app.support.disputes.errors.too-many-requests');
            case 500:
              throw new Error('app.support.disputes.errors.server-error');
            case 502:
            case 503:
            case 504:
              throw new Error('app.support.disputes.errors.service-unavailable');
            default:
              throw new Error(data?.message || 'app.support.disputes.errors.unknown');
          }
        } else if (error.request) {
          // Network error
          throw new Error('app.support.disputes.errors.network-error');
        } else {
          // Other error
          throw new Error(error.message || 'app.support.disputes.errors.unknown');
        }
      })
  );
